import {controller, target, targets} from '@github/catalyst'
import {TemplateInstance} from '@github/template-parts'

@controller('affected-product-row')
export default class AffectedProductRowElement extends HTMLElement {
  @target declare destroyInput: HTMLInputElement
  @target declare ecosystemOtherInputElement: HTMLInputElement
  @target declare ecosystemSelectElement: HTMLSelectElement
  @target declare packageInputElement: HTMLInputElement
  @target declare unsupportedEcosystemMessageElement: HTMLElement
  @targets declare inputs: Array<HTMLInputElement | HTMLSelectElement>
  @target declare packageUrlElement: HTMLElement

  connectedCallback() {
    // When choosing `Other`, navigating elsewhere, then clicking the browser back
    // button, the browser remembers the value for the select, but it does not
    // change the hidden attribute and make the input type text shown.
    // The timeout is necessary because the prefilling of the value might not
    // occur until this enclosing function execution is over.
    setTimeout(() => {
      if (this.ecosystemOtherInputElement) {
        this.ecosystemOtherInputElement.hidden = this.ecosystemSelectElement.value !== 'other'
      }
    }, 0)
    setTimeout(() => {
      this.showUnsupportedEcosystemMessage()
    })
    setTimeout(() => {
      this.showPackageUrlElement()
    })
  }

  clear() {
    for (const element of this.inputs) {
      element.value = ''
    }
    this.ecosystem = this.initialEcosystem
  }

  delete() {
    this.dispatchEvent(new CustomEvent('affected-product-row-delete', {bubbles: true, detail: this}))
  }

  focusFirstInput() {
    this.ecosystemSelectElement.focus()
  }

  hide() {
    this.hidden = true
    for (const input of this.inputs) {
      // So the enclosing form doesn't become invalid due to hidden fields that are required
      input.required = false
    }
  }

  get ecosystem(): string {
    return this.ecosystemSelectElement.value
  }

  set ecosystem(ecosystemValue: string) {
    this.ecosystemSelectElement.value = ecosystemValue
    this.ecosystemOtherInputElement.hidden = ecosystemValue !== 'other'
  }

  get ecosystemOther(): string {
    return this.ecosystemOtherInputElement.value
  }

  set ecosystemOther(ecosystemValue: string) {
    this.ecosystemOtherInputElement.value = ecosystemValue
  }

  get initialEcosystem() {
    return this.getAttribute('data-initial-ecosystem') || ''
  }

  get package(): string {
    return this.packageInputElement.value
  }

  get rowIndex(): string {
    return this.getAttribute('data-row-index') || ''
  }

  set package(packageValue: string) {
    this.packageInputElement.value = packageValue
  }

  markToDestroy() {
    this.destroyInput.value = 'true'
  }

  handleInputBlur(event: InputEvent) {
    const input = event.target as HTMLInputElement
    const erroredFormGroup = input.closest('dl.form-group.errored')
    if (erroredFormGroup) {
      erroredFormGroup.classList.remove('errored')
    }
  }

  showUnsupportedEcosystemMessage() {
    if (!this.unsupportedEcosystemMessageElement) return

    if (this.ecosystemSelectElement.value) {
      this.unsupportedEcosystemMessageElement.hidden = this.ecosystemSelectElement.value === 'pip'
      return
    }
    this.unsupportedEcosystemMessageElement.hidden = true
  }

  async showPackageUrlElement() {
    const selectedItem = this.ecosystemSelectElement
    const ecosystem = selectedItem.value
    const packageName = this.packageInputElement.value

    if (!ecosystem || !packageName) {
      this.packageUrlElement.replaceChildren()
      return
    }

    const url = `/advisories/package-url?ecosystem=${ecosystem}&package_name=${packageName}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    })
    const data = await response.text()
    const res = data ? JSON.parse(data) : {}

    const ecosystemLabel = selectedItem?.options[selectedItem?.selectedIndex]?.innerHTML

    if (response.ok && res['package_url']) {
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const packageLinkFoundTemplate = document.querySelector<HTMLTemplateElement>(
        '[data-target="affected-product-row.packageLinkFoundTemplate"]',
      )!
      const urlFoundTemplate = new TemplateInstance(packageLinkFoundTemplate, {
        packageUrl: res['package_url'],
        ecosystemLabel,
      })
      this.packageInputElement.setAttribute('aria-describedby', `package-url-msg-${this.rowIndex}`)
      this.ecosystemSelectElement.setAttribute('aria-describedby', `package-url-msg-${this.rowIndex}`)
      this.packageUrlElement.replaceChildren(urlFoundTemplate)
    } else if (res['error'] === 'not_found') {
      // eslint-disable-next-line i18n-text/no-en
      const packageNotFoundText = `Exact package name not found on ${ecosystemLabel}. You can still submit this report.`
      /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
      const packageLinkNotFoundTemplate = document.querySelector<HTMLTemplateElement>(
        '[data-target="affected-product-row.packageLinkNotFoundTemplate"]',
      )!
      const urlNotFoundTemplate = new TemplateInstance(packageLinkNotFoundTemplate, {
        packageNotFoundText,
      })
      this.packageInputElement.setAttribute('aria-describedby', `package-url-msg-${this.rowIndex}`)
      this.ecosystemSelectElement.setAttribute('aria-describedby', `package-url-msg-${this.rowIndex}`)
      this.packageUrlElement.replaceChildren(urlNotFoundTemplate)
    } else {
      this.packageInputElement.removeAttribute('aria-describedby')
      this.ecosystemSelectElement.removeAttribute('aria-describedby')
      this.packageUrlElement.replaceChildren()
    }
  }
}
